import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import type { EntityPickerOperation } from '@adornis/entity-picker/entity-picker-operation.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useMemo, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { CardLoader } from '@martinus/styles/client/PageLoader.js';
import { html, nothing } from 'lit';
import { Tarifgruppe } from '../../contracts/enums/Tarifgruppe.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { School } from '../../groups/entities/School.js';
import { NoPermission } from '../../permissions/client/NoPermission.js';
import { Permission } from '../../permissions/roles.js';
import type { Person } from '../../users/entities/person.js';
import { BeschaeftigungsGenehmigung } from '../entities/beschaeftigungs-genehmigung.js';

const permitSelectionSet = {
  _id: 1,
  _class: 1,
  userID: 1,
  schulID: 1,
  beantragtAm: 1,
  genehmigtAm: 1,
  beantragtVon: 1,
  befristetBis: 1,
  tarifgruppe: 1,
  fach: 1,
  bemerkung: 1,
  createdAt: 1,
} satisfies BaseQLSelectionSet<BeschaeftigungsGenehmigung>;

type EmploymentPermit = NarrowedEntity<BeschaeftigungsGenehmigung, typeof permitSelectionSet>;

/** Shell used for every non-form state (no permission, loading, error, empty) so the column keeps its geometry. */
const detailsShell = (content: Renderable) => html`<div class="m-4 flex grow flex-col">${content}</div>`;

const formatDay = (day: Maybe<Date>, fallback: string) => (day ? formatCalendarDay(day, 'dd.MM.yyyy') : fallback);

const EmploymentPermitDetailsForm = stateful((props: { permit: EmploymentPermit; canWrite: boolean }) => {
  // The form edits a plain snapshot via `toObject()`; its day fields are the canonical JS Dates that
  // `DateInput({ type: 'date' })` produces and consumes. `constructValue` turns the snapshot back
  // into the entity right before saving.
  const permitData = useMemo(() => [props.permit.toObject()], [props.permit._id]);

  const form = useForm(
    permitData,
    {
      validator: BeschaeftigungsGenehmigung.prototype.validator.functions,
      // without write permission the whole form is read only instead of silently unsaveable
      enabledFields: props.canWrite ? undefined : {},
    },
    [permitData, props.canWrite],
  );

  // stable reference: `EntityPickerInput` writes the search string onto the filter it is handed
  const schoolFilter = useMemo(
    () => [
      new AdornisFilter({
        aspects: [
          new StringAspect({ fieldName: '_class', value: School._class, mode: 'equals' }),
          // `customID` aspects are only read by operations that explicitly look them up - a plain
          // field match needs `fieldName`, otherwise `getMongoFilter()` returns null and every
          // group passes as a school.
          new BooleanAspect({ fieldName: 'isSgSchool', value: true }),
        ],
      }),
    ],
    [],
  );

  const handleSave = async () => {
    if (!props.canWrite) return;

    const validationError = await form.validate();
    if (validationError) return;

    await guardDialog(
      async () => {
        const permit = constructValue<BeschaeftigungsGenehmigung>(form.document);
        await permit.save();
      },
      { successText: 'Beschäftigungsgenehmigung wurde erfolgreich gespeichert!' },
    );
  };

  const befristetBis = form.document.befristetBis;
  // start/end are inclusive days, so the permit is only expired once its last day has passed
  const isExpired = !!befristetBis && befristetBis.getTime() < todayAsCalendarDay().getTime();

  return html`
    <div class="m-4 flex grow flex-col">
      <div class="card card-body">
        ${CardHeader({
          title: 'Beschäftigungsgenehmigung',
          subtitle: html`
            <span class="flex flex-wrap items-center gap-2">
              <span>
                ${formatDay(form.document.beantragtVon, '<Startdatum>')} bis ${formatDay(befristetBis, '<Enddatum>')}
              </span>
              ${isExpired ? html`<span class="badge badge-sm badge-error">Abgelaufen</span>` : nothing}
            </span>
          `,
        })}

        <div class="form-grid">
          ${FormGridEntry({
            label: 'Schule',
            render: EntityPickerInput({
              ...form.field('schulID'),
              placeholder: 'Keine Schule hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
              class: Group,
              // the API returns NarrowedEntity results, which the picker's nominal entity typing rejects
              operation: GroupsAPI.subscribeGroups as unknown as EntityPickerOperation<Group, [AdornisFilter]>,
              filter: schoolFilter,
              selectionSet: { name: 1, _id: 1 },
            }),
          })}
          ${FormGridEntry({
            label: 'Beantragt am',
            render: DateInput({
              ...form.field('beantragtAm'),
              type: 'date',
              placeholder: 'Kein Datum hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Genehmigt am',
            render: DateInput({
              ...form.field('genehmigtAm'),
              type: 'date',
              placeholder: 'Kein Datum hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Beantragt von',
            render: DateInput({
              ...form.field('beantragtVon'),
              type: 'date',
              placeholder: 'Kein Datum hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Befristet bis',
            render: DateInput({
              ...form.field('befristetBis'),
              type: 'date',
              placeholder: 'Kein Datum hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Tarifgruppe',
            render: DropdownSelectionInput({
              ...form.field('tarifgruppe'),
              selectables: Object.values(Tarifgruppe),
              placeholder: 'Keine Tarifgruppe hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Fach',
            render: TextInput({
              ...form.field('fach'),
              placeholder: 'Kein Fach hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
          ${FormGridEntry({
            label: 'Bemerkung',
            render: TextInput({
              ...form.field('bemerkung'),
              placeholder: 'Keine Bemerkung hinterlegt',
              placeholderMode: 'hide',
              clearable: true,
            }),
          })}
        </div>

        ${
          props.canWrite
            ? html`
                <div class="flex justify-end">
                  <button class="btn btn-primary btn-lg" @click=${handleSave}>
                    <span class="icon-[material-symbols--save-outline]"></span>
                    Speichern
                  </button>
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
});

export const SGEmploymentPermitDetails = stateful((props: { permitID: Maybe<string> }) => {
  const user = useObservable(() => currentUser<Person>(), []);
  const canRead = !!user?.hasPermission(Permission.AG_SEKTION_R);
  const canWrite = !!user?.hasPermission(Permission.AG_SEKTION_W);

  const {
    value: permit,
    loading,
    error,
  } = useQuery(
    () =>
      canRead && props.permitID
        ? getByID(BeschaeftigungsGenehmigung, props.permitID)(permitSelectionSet)
        : Promise.resolve(null),
    [props.permitID, canRead],
  );

  if (!canRead) return detailsShell(NoPermission({ message: 'Sie haben nicht die Berechtigung diese Seite zu sehen' }));

  if (!props.permitID)
    return detailsShell(html`
      <div class="card card-body items-center justify-center text-center text-base-content/70">
        <span class="icon-[material-symbols--badge-outline] h-10 w-10"></span>
        <p>Keine Beschäftigungsgenehmigung ausgewählt.</p>
      </div>
    `);

  if (loading) return detailsShell(CardLoader({ title: 'Beschäftigungsgenehmigung', elements: 8 }));

  if (error)
    return detailsShell(html`
      <div class="card card-body">
        <div class="alert alert-error">
          <span class="icon-[material-symbols--error-outline]"></span>
          <span>Die Beschäftigungsgenehmigung konnte nicht geladen werden: ${error.message}</span>
        </div>
      </div>
    `);

  if (!permit)
    return detailsShell(html`
      <div class="card card-body items-center justify-center text-center text-base-content/70">
        <span class="icon-[material-symbols--search-off] h-10 w-10"></span>
        <p>Diese Beschäftigungsgenehmigung existiert nicht (mehr).</p>
      </div>
    `);

  return EmploymentPermitDetailsForm({ permit, canWrite });
});
